/* ------------------------------------------------------------
  UWP Option Help Modal (Horizon + SC Product Options)
  - Reads JSON from <script id="uwp-option-helps" type="application/json">
  - Injects "詳細" button next to each SCPO option title
  - Opens <dialog id="uwpOptionModal"> and renders help content
  NOTE:
    - "選択する" 連動は削除（出しません）
    - 画像がない item は画像枠を出さず、テキストを全幅で表示
------------------------------------------------------------ */

(() => {
  const JSON_ID = 'uwp-option-helps';
  const MODAL_ID = 'uwpOptionModal';
  const MODAL_TITLE_ID = 'uwpOptionModalTitle';
  const MODAL_BODY_ID = 'uwpOptionModalBody';

  function safeParseJSON() {
    const el = document.getElementById(JSON_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      console.warn('[uwp-option-modal] JSON parse failed:', e);
      return null;
    }
  }

  // SCPO側の「必須 *」や全角/半角スペース差を吸収
  function normalizeTitle(s) {
    return String(s ?? '')
      .replace(/[＊*]/g, '')     // * / ＊ を除去
      .replace(/\s+/g, '')       // 連続空白を除去
      .trim();
  }

  function getHelps() {
    const data = safeParseJSON();
    const helps = data?.helps;
    return Array.isArray(helps) ? helps : [];
  }

  function findHelpByOptionTitle(optionTitle) {
    const key = normalizeTitle(optionTitle);
    if (!key) return null;

    const helps = getHelps();
    return helps.find(h => normalizeTitle(h?.title) === key) || null;
  }

  function ensureModal() {
    const modal = document.getElementById(MODAL_ID);
    if (!modal) return null;

    // close buttons
    modal.querySelectorAll('[data-uwp-modal-close]').forEach(btn => {
      if (btn.__uwpBound) return;
      btn.__uwpBound = true;
      btn.addEventListener('click', () => modal.close());
    });

    // backdrop click (dialog外側クリックで閉じる)
    if (!modal.__uwpBackdropBound) {
      modal.__uwpBackdropBound = true;
      modal.addEventListener('click', (e) => {
        // dialog自体（外側）をクリックしたら閉じる
        const rect = modal.getBoundingClientRect();
        const inDialog =
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom;

        // ただし、内側の要素クリックは閉じない
        if (e.target === modal && inDialog) {
          // dialog要素そのものクリックでも inDialog は true になるので
          // target判定だけでOK（内側は target が別）
          modal.close();
        }
      });
    }

    return modal;
  }

  function escHtml(s) {
    return String(s ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  // Liquid側で newline_to_br 済みの想定（intro/body）
  function htmlFromTrustedLiquid(str) {
    // JSON内は既に <br> が入っているのでそのまま扱う（ここではエスケープしない）
    // ただし null/undefined は空に
    return String(str ?? '');
  }

  function renderHelp(help) {
    const titleEl = document.getElementById(MODAL_TITLE_ID);
    const bodyEl = document.getElementById(MODAL_BODY_ID);
    if (!titleEl || !bodyEl) return;

    titleEl.textContent = help?.title || '詳細';

    const intro = htmlFromTrustedLiquid(help?.intro);
    const items = Array.isArray(help?.items) ? help.items : [];

    let html = '';

    if (intro) {
      html += `<div class="uwp-help-intro">${intro}</div>`;
    }

    if (items.length) {
      html += `<div class="uwp-help-cards">`;

      for (const it of items) {
        const itTitle = escHtml(it?.title || '');
        const body = htmlFromTrustedLiquid(it?.body);
        const priceText = escHtml(it?.price_text || '');
        const note = escHtml(it?.note || '');
        const linkUrl = String(it?.link_url || '').trim();
        const img = it?.image;

        const hasImage = !!img;
        const cardClass = hasImage ? 'uwp-help-card' : 'uwp-help-card uwp-help-card--no-media';

        html += `<div class="${cardClass}">`;

        if (hasImage) {
          html += `
            <div class="uwp-help-card__media">
              <img src="${escHtml(img)}" alt="${itTitle}">
            </div>
          `;
        }

        html += `<div class="uwp-help-card__content">`;

        if (itTitle) html += `<div class="uwp-help-card__title">${itTitle}</div>`;
        if (body) html += `<div class="uwp-help-card__body">${body}</div>`;
        if (priceText) html += `<div class="uwp-help-card__price">${priceText}</div>`;
        if (note) html += `<div class="uwp-help-card__note">${note}</div>`;

        // actions（「選択する」は出さない。リンクだけ）
        const hasLink = !!linkUrl;
        if (hasLink) {
          html += `<div class="uwp-help-card__actions">`;
          html += `<a class="uwp-help-card__link" href="${escHtml(linkUrl)}" target="_blank" rel="noopener">関連ページ</a>`;
          html += `</div>`;
        }

        html += `</div>`; // content
        html += `</div>`; // card
      }

      html += `</div>`; // cards
    }

    bodyEl.innerHTML = html || '';
  }

  function openHelpForTitle(optionTitle) {
    const help = findHelpByOptionTitle(optionTitle);
    if (!help) return;

    const modal = ensureModal();
    if (!modal) return;

    renderHelp(help);

    if (typeof modal.showModal === 'function') {
      modal.showModal();
    } else {
      // 古いブラウザ向け（念のため）
      modal.setAttribute('open', 'open');
    }
  }

  function injectButtonsOnce(root = document) {
    // SCPOのオプションブロックを探す（クラスは現状のDOMに合わせて“広め”に拾う）
    const blocks = root.querySelectorAll('.bold_option, .bold_options .bold_option');
    if (!blocks.length) return;

    blocks.forEach(block => {
      // 代表的なタイトル要素候補
      const titleEl =
        block.querySelector('.bold_option_title') ||
        block.querySelector('.bold_option_swatch_title') ||
        block.querySelector('[class*="option_title"]');

      if (!titleEl) return;

      // 既にボタンが入ってたらスキップ
      if (titleEl.parentElement?.querySelector('.uwp-opt-help-btn')) return;

      const optionTitle = (titleEl.textContent || '').trim();
      if (!optionTitle) return;

      // 対応するhelpが無いなら入れない（余計な「詳細」を出さない）
      const help = findHelpByOptionTitle(optionTitle);
      if (!help) return;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'uwp-opt-help-btn';
      btn.setAttribute('aria-label', '詳細を表示');
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openHelpForTitle(optionTitle);
      });

      // タイトルの“直後”に差し込む
      titleEl.insertAdjacentElement('afterend', btn);
    });
  }

  // 初期実行
  function init() {
    injectButtonsOnce(document);

    // DOMが後から差し替わるので監視
    const mo = new MutationObserver(() => {
      // 連続発火を抑える
      if (init.__t) cancelAnimationFrame(init.__t);
      init.__t = requestAnimationFrame(() => injectButtonsOnce(document));
    });

    mo.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
